"use client";
import { useEffect, useState } from "react";
import { generatePaymentCallbackUrl } from "@/lib/utils";
import Portal from "../globalPortal";
import {
  IBaseModalProps,
  IIncreaseWalletDepositPayload,
  IPackages,
  TPurchasePackageRequest,
  TRawServiceApiKeys,
  TServiceNavigationItem,
} from "@/interfaces";
import { useLockBodyScroll } from "@uidotdev/usehooks";
import {
  useGetInquiry,
  useGetWalletBalance,
  useIncreaseWalletDepositMutation,
  usePurchasePackageMutation,
} from "@/hooks";
import { useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { useAppStore, useSecureStore } from "@/store";
import { useToastStore } from "@/store/toastStore";
import { routes } from "@/routes";
import SufficientBalanceModal from "./sufficientBalanceModal";
import styles from "./paymentCheckout.module.css";
import Logo from "@/components/logo";

interface Props extends IBaseModalProps {
  selectedService?: TServiceNavigationItem;
  submissionError: Error | null;
  submissionLoading: boolean;
  showManualError?: string | null;
  persistedData?: Record<string, any>;
  onErrorReset?: () => void;
  payBtnId?: string;
  selectedPkg: IPackages | null;
  paymentSubmission?: () => void;
}

export default function PackagePaymentModal({
  isOpen,
  onClose,
  selectedService,
  showManualError = null,
  submissionLoading = false,
  persistedData,
  onErrorReset,
  payBtnId,
  selectedPkg,
}: Props) {
  const pathname = usePathname();
  const { data: session } = useSession();
  const router = useRouter();
  const {
    data: walletBalanceData,
    isLoading: isWalletBalanceLoading,
    error: walletBalanceError,
  } = useGetWalletBalance(session?.user.token);

  const { data: inquiryData, isLoading: isInquiryLoading } = useGetInquiry({
    inquiry: (selectedService?.apiKey as TRawServiceApiKeys) || "",
    token: session?.user.token,
  });

  const { mutate: purchasePackageFn, isPending: isPurchasing } =
    usePurchasePackageMutation();

  const { setOpenToast } = useToastStore();

  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const { setPersistedFields } = useSecureStore();

  const {
    isPending: isIncreaseSubmitting,
    mutate: increaseWalletDepositFn,
    error: increaseSubmittingError,
    reset: resetIncreaseWalletDeposit,
  } = useIncreaseWalletDepositMutation(session?.user.token);

  const [isUseWallet, setIsUseWallet] = useState(false);
  const walletBalance = Number(walletBalanceData?.data?.balance || 0);
  const serviceCost = Number(inquiryData?.data?.price_user || 0);

  useLockBodyScroll();

  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset the mutation error
      resetIncreaseWalletDeposit();
      // Reset parent component errors
      if (onErrorReset) {
        onErrorReset();
      }
    }
  }, [isOpen, resetIncreaseWalletDeposit, onErrorReset]);

  // Reset errors when component unmounts
  useEffect(() => {
    return () => {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    };
  }, [resetIncreaseWalletDeposit, onErrorReset]);

  const handleCreditIncrementSubmission = (
    data: TPurchasePackageRequest & { is_topup?: boolean },
    bank?: string,
  ) => {
    const payload: IIncreaseWalletDepositPayload = {
      amount: data.amount,
      callback_url: generatePaymentCallbackUrl(pathName),
      use_wallet: data.use_wallet,
      items: data.items,
      is_topup: data.is_topup,
    };
    increaseWalletDepositFn(
      { payload, bank },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            onClose();
            if (persistedData) {
              setPersistedFields(persistedData);
            }
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };

  const handlePackagePayment = (data?: {
    bank?: string;
    digit?: string;
    topup?: boolean;
  }) => {
    if (data?.topup) {
      handleCreditIncrementSubmission(
        {
          amount: Number(data.digit),
          use_wallet: false,
          callback: "",
          items: {},
          is_topup: true,
        },
        data.bank,
      );
      return;
    }

    if (!session?.user.token) {
      setOpenToast({
        variant: "default",
        message: "برای خرید بسته ابتدا وارد شوید",
      });
      return;
    }
    if (selectedPkg?.price) {
      // Extract numeric value from price string (remove non-digit characters)
      const numericPrice = selectedPkg.price.replace(/[^\d]/g, "");
      const isWalletGreater = walletBalance >= Number(numericPrice);

      if (isUseWallet) {
        // اگه مبلغ کیف بیشتر از قیمت پکیج بود و میخواست که از کیف کم بشه
        if (isWalletGreater) {
          purchasePackageFn(
            {
              purchasedPackageId: Number(selectedPkg?.id),
              token: session?.user.token,
            },
            {
              onSuccess: (res) => {
                setOpenToast({
                  variant: "success",
                  message: "خرید بسته با موفقیت انجام شد",
                });
                onClose();
                router.push(routes.main.PACKAGES.primaryPath);
              },
              onError: (err) => {
                console.error("خطا در خرید پکیج", err);
              },
            },
          );
        } else {
          const finalPrice = Number(numericPrice) - walletBalance;
          const paymentData = {
            amount: finalPrice < 1000 ? 1000 : finalPrice,
            use_wallet: true,
            callback: "",
            items: {
              campaign: selectedPkg.id,
            },
          };
          handleCreditIncrementSubmission(paymentData, data?.bank);
        }
      } else {
        const paymentData = {
          amount: Number(numericPrice),
          use_wallet: false,
          callback: "",
          items: {
            campaign: selectedPkg.id,
          },
        };
        handleCreditIncrementSubmission(paymentData, data?.bank);
      }
    }
  };

  if (!isOpen || isWalletBalanceLoading || isInquiryLoading) return null;
  return (
    <Portal>
      <div className={styles.overlay} onMouseDown={onClose}>
        <section
          className={`${styles.checkout} ${styles.singleCheckout}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="package-payment-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className={styles.header}>
            <div className={styles.headerTitle}>
              <span className={styles.brandMark}>
                <Logo width={35} height={35} />
              </span>
              <div>
                <h2 id="package-payment-title">تأیید و پرداخت</h2>
                <p>{selectedPkg?.title || "بسته استعلام"}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <span className={styles.secure}>پرداخت امن</span>
              <button
                className={styles.close}
                type="button"
                aria-label="بستن پنجره پرداخت"
                onClick={onClose}
              >
                <span className={styles.closeGlyph} aria-hidden="true" />
              </button>
            </div>
          </header>
          <div className={styles.body}>
            <section className={styles.summaryPanel}>
              <SufficientBalanceModal
                payBtnId={payBtnId}
                onClose={onClose}
                onSubmit={handlePackagePayment}
                serviceType={selectedPkg?.title || "بسته استعلام"}
                walletBalance={walletBalanceData?.data?.balance}
                paymentType={walletBalanceData?.data?.payment_type}
                inquiryPrice={String(serviceCost)}
                error={walletBalanceError || increaseSubmittingError}
                isLoading={
                  submissionLoading || isIncreaseSubmitting || isPurchasing
                }
                showManualError={showManualError}
                selectedPackage={selectedPkg || undefined}
                useWallet={isUseWallet}
                walletHandler={setIsUseWallet}
              />
            </section>
          </div>
        </section>
      </div>
    </Portal>
  );
}
